import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { makeStyles, useTheme } from '@mui/styles'
import PropTypes from 'prop-types'
import lang from '@/lang'
import Decimal from 'decimal.js'
import clsx from 'clsx'

import { fetchBankAccountList } from '@/redux/ducks/banking/bankAccountList'
import { setBankTransferFormData, resetBankTransferFormData } from '@/redux/ducks/banking/bankTransfer'

import {
  CurrencyInput,
  SelectInput,
  TextareaInput,
  Checkbox,
  TextInput,
} from '@/modules/base/components/inputs'
import { FINANCE_API } from '@/modules/banking/constants'
import checkValidSepaInput from '@/modules/receipt/_helpers/checkValidSepaInput'
import ContactSetupRequiredEmbeddedDialog from '@/modules/banking/components/list/ContactSetupRequiredEmbeddedDialog'
import SupplierCustomerSelect from '@/modules/base/components/generics/supplier-customer-select/supplier-customer-select'
import BankTransferInfo from './bank-transfer-info/bank-transfer-info'
import { BASE_DATA_TYPES } from '@/modules/base/components/generics/constants'
import { transactionTypes } from '@/modules/base/util/constants'
import PaymentFormModel from '@/modules/payment/model/PaymentFormModel'
import CustomerService from '@/modules/customer/customerService'
import SupplierService from '@/modules/supplier/supplierService'
import { urls } from '@/modules/banking/config'
import { formatIbanGroups } from '@/modules/base/components/inputs/iban-input/iban-input'
import BankingService from '@/modules/_services/banking.service'
import { uniqBy } from 'lodash'

const {
  TRANSACTION_TYPE_EXPENSE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
} = transactionTypes

const {
  ACCOUNT_TYPE: {
    CASH_JOURNAL,
    CLEARING_ACCOUNT,
    PAYPAL,
    CLEARING_ACCOUNT_PAYPAL,
    CASH_JOURNAL_POS,
    CLEARING_ACCOUNT_POS,
    SHOPIFY_POS,
    SHOPIFY_PAYMENTS,
    OFFLINE_ACCOUNT,
    CREDIT_CARD,
  },
} = FINANCE_API
const EXCLUDED_ACCOUNT_TYPES = [
  CASH_JOURNAL,
  CLEARING_ACCOUNT,
  PAYPAL,
  CLEARING_ACCOUNT_PAYPAL,
  CASH_JOURNAL_POS,
  CLEARING_ACCOUNT_POS,
  SHOPIFY_POS,
  SHOPIFY_PAYMENTS,
  OFFLINE_ACCOUNT,
  CREDIT_CARD,
]

const dataQsIdPrefix = 'bankTransferForm'
const stripIban = (value = '') => (value || '').replace(/\s/gm, '')

const getOcrPayeeIban = (transaction) =>
  (transaction?.receipts || []).find(receipt => receipt?.metaData?.ocr?.payeeIban)?.metaData?.ocr?.payeeIban || ''

const getOcrBankAccounts = (transaction) => {
  if (!transaction) { return [] }
  const { receipts, filesToAssign } = transaction
  const files = [...(receipts?.length ? receipts : []), ...(filesToAssign?.length ? filesToAssign : [])]

  return (files)
    .find(file => file?.metaData?.ocr?.bankAccounts)?.metaData?.ocr?.bankAccounts || []
}

const buildIbanOptions = (bankAccounts, type) => {
  let labelSuffix
  switch (type) {
    case BASE_DATA_TYPES.CUSTOMER: {
      labelSuffix = lang.payments.bankTransferDrawer.bankTransferForm.iban.optionSuffix.customer
      break
    }
    case BASE_DATA_TYPES.SUPPLIER: {
      labelSuffix = lang.payments.bankTransferDrawer.bankTransferForm.iban.optionSuffix.supplier
      break
    }
    default: {
      labelSuffix = ''
      break
    }
  }

  return (bankAccounts || [])
    .filter(bankAccount => bankAccount?.iban)
    .map(bankAccount => ({
      value: stripIban(bankAccount.iban),
      label: formatIbanGroups(bankAccount.iban),
      labelSuffix,
    }))
}

const prepareImageSource = (url) => {
  return url.startsWith('http')
    ? url
    : `${urls.resource}${url}`
}

const createBankAccountOption = (bankAccount) => {
  return bankAccount
    ? { value: bankAccount.id, label: `${bankAccount.accountName} (${bankAccount.accountIban ? formatIbanGroups(bankAccount.accountIban) : ''})`, iconUrl: prepareImageSource(bankAccount.bankLogoSmall) }
    : null
}

const getDefaultValue = (transaction) => {
  // When the payee was picked/changed in the receipt form, addPayee stores the full select option
  // under `value` (with the correct customer/supplier type). The legacy supplierId/customerId fields
  // are NOT updated on such a change, so prefer `value` to avoid showing a stale payee.
  if (transaction?.value?.type) {
    const { value, label, data, type } = transaction.value
    return { value, label, data, type }
  }

  switch (transaction.type) {
    case TRANSACTION_TYPE_EXPENSE:
      return transaction.supplierId
        ? { value: transaction.supplierId, label: transaction.payee || transaction.supplier.name, data: transaction.supplier, type: BASE_DATA_TYPES.SUPPLIER }
        : transaction.customerId
          ? { value: transaction.customerId, label: transaction.payee || transaction.customer.name, data: transaction.customer, type: BASE_DATA_TYPES.CUSTOMER }
          : null
    default:
      return transaction.customerId
        ? { value: transaction.customerId, label: transaction?.customerData?.name, data: transaction.customer, type: BASE_DATA_TYPES.CUSTOMER }
        : null
  }
}

const buildPayment = (transaction) => {
  if (transaction.type === TRANSACTION_TYPE_EXPENSE) {
    return PaymentFormModel.fromExpense(transaction)
  } else if (transaction.type === TRANSACTION_TYPE_MANUAL_CREDIT_NOTE) {
    return PaymentFormModel.fromManualCreditNote(transaction)
  } else {
    return PaymentFormModel.fromCreditNote(transaction)
  }
}

const getInitialFormData = (transaction) => {
  const payment = buildPayment(transaction)
  return {
    amount: transaction.type === TRANSACTION_TYPE_EXPENSE ? transaction.outstandingAmount : new Decimal(transaction.outstandingAmount).times(-1).toNumber(),
    accountHolder: payment.customerName,
    payee: payment.customerName,
    usage: payment.notes,
  }
}

// The supplier/customer list endpoint feeding SupplierCustomerSelect does not include bank
// accounts; only getById returns them. So we always fetch the full record by id and type.
const fetchPayeeBankAccounts = async(id, type) => {
  if (!id) { return null }
  if (type === BASE_DATA_TYPES.CUSTOMER) {
    const customer = await CustomerService.getById(id)
    return customer.bankAccounts
  } else if (type === BASE_DATA_TYPES.SUPPLIER) {
    const supplier = await SupplierService.getById(id)
    return supplier.bankAccounts
  }
  return null
}

const fetchSupplierCustomerBankAccounts = (transaction) => {
  // Resolve the currently selected payee (prefers the edited `value`, falls back to supplierId/customerId).
  const payee = getDefaultValue(transaction)
  if (payee?.value && payee?.type) {
    return fetchPayeeBankAccounts(payee.value, payee.type)
  }
  return Promise.resolve(null)
}

const useStyles = makeStyles((theme) => ({
  currencyInput: {
    '& > *:nth-child(2)': {
      fontWeight: 500,
    },
  },
  checkBox: {
    visibility: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 27,
  },
  checkboxLabel: {
    marginLeft: 15,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  },
  defaultContainer: {
    position: 'relative',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '12px 0',

    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },

    '& > *': {
      flex: '1 1 0',
      margin: '0 8px',

      '&:first-child': {
        marginLeft: 0,
      },
      '&:last-child': {
        marginRight: 0,
      },
    },
  },

  bankAccount: {
    flex: '1 1 0',
  },
  usage: {
    flex: '1 1 100%',
    width: '100%',
  },
  ibanRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  creditInstitutionWrapper: {
    fontSize: theme.fontSizes?.smaller,
    paddingTop: 10,
    display: 'flex',
    gap: 5,
  },
  creditInstitutionLabel: {
    color: theme.darkerGrey,
  },
  bankName: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 300,
    overflow: 'hidden',
  },
  highlight: {
    '& > * > input': {
      borderColor: theme.colors.yellow,
      background: theme.yellowBackground,
    },
  },
}))

const BankTransferForm = (props) => {
  const dispatch = useDispatch()
  const store = useStore()
  const theme = useTheme()
  const classes = useStyles()
  const { transaction } = props

  // Read the persisted form data once at mount via the store (no subscription -> no re-renders).
  // The form is the source of this data and pushes it back via the sync effect below.
  const [formData, setFormData] = useState(() => {
    const { bic, isIbanValid, ...importantData } = store.getState().banking.bankTransfer.data
    return { ...importantData, ...getInitialFormData(transaction), bankAccount: null }
  })
  // Always mirrors the latest committed formData so async handlers can read the freshest value
  // (e.g. an IBAN the user changes while a payee fetch is in flight) without a stale closure.
  const formDataRef = useRef(formData)
  useEffect(() => { formDataRef.current = formData })
  const [bic, setBic] = useState(() => store.getState().banking.bankTransfer.data.bic)
  const [isIbanValid, setIsIbanValid] = useState(() => store.getState().banking.bankTransfer.data.isIbanValid)
  const [inputErrors, setInputErrors] = useState({})
  const [supplierInputValue, setSupplierInputValue] = useState('')
  const [supplierCustomerSelectValue, setSupplierCustomerSelectValue] = useState(() => getDefaultValue(transaction))
  const [ibanOptions, setIbanOptions] = useState([])
  const [extraIbanOptions, setExtraIbanOptions] = useState([])
  const [ibanError, setIbanError] = useState(null)
  const [bankName, setBankName] = useState(null)
  const [highlightDismissed, setHighlightDismissed] = useState({})
  const [autoFilledFields, setAutoFilledFields] = useState(() => {
    const { accountHolder } = getInitialFormData(transaction)
    return (accountHolder || transaction?.payeeName) ? { accountHolder: true } : {}
  })

  const markAutoFilled = useCallback((fields) => {
    setAutoFilledFields(prev => ({ ...prev, ...fields }))
    setHighlightDismissed(prev => {
      const cleared = Object.keys(fields).filter(key => fields[key])
      if (!cleared.length) return prev
      const next = { ...prev }
      cleared.forEach(key => { next[key] = false })
      return next
    })
  }, [])

  // Registers an IBAN as a permanently available option (prefilled or manually entered).
  const addExtraIbanOption = useCallback((rawIban) => {
    const value = stripIban(rawIban || '')
    if (!value) return
    setExtraIbanOptions(prev => prev.some(option => option.value === value)
      ? prev
      : [...prev, { value, label: formatIbanGroups(value) }])
  }, [])

  const customSelectOptions = useMemo(() => ({
    labelSuffixDelimiter: ' ',
    isClearable: true,
    placeholder: lang.payments.bankTransferDrawer.bankTransferForm.iban.placeholder,
  }), [])

  useEffect(() => {
    fetchSupplierCustomerBankAccounts(transaction).then(bankAccounts => {
      const type = getDefaultValue(transaction)?.type || null

      const customerOrSupplierIbans = buildIbanOptions(bankAccounts, type)
      const transactionBankAccounts = transaction.bankAccounts ? buildIbanOptions(transaction?.bankAccounts) : []
      const ocrBankAccounts = buildIbanOptions(getOcrBankAccounts(transaction)) || []
      const ibanOptions = uniqBy([...customerOrSupplierIbans, ...transactionBankAccounts, ...ocrBankAccounts], 'value')
      setIbanOptions(ibanOptions)

      const ocrIban = getOcrPayeeIban(transaction)
      let iban
      if (transaction?.payeeIban) {
        iban = transaction.payeeIban
      } else if (ocrIban) {
        iban = ocrIban
      } else if (bankAccounts?.length) {
        iban = bankAccounts[0].iban
      } else {
        iban = transaction.iban || ''
      }

      const cleanIban = stripIban(iban || '')
      setFormData(prev => {
        const next = { ...prev, iban: cleanIban }
        if (!transaction?.payeeIban && bankAccounts?.length && bankAccounts[0].accountHolder) {
          next.accountHolder = bankAccounts[0].accountHolder
        }
        return next
      })
      markAutoFilled({ iban: !!cleanIban })
      addExtraIbanOption(cleanIban)
    })
  }, [])

  // Validates the current IBAN (prefilled, selected or manually entered) and
  // sets bic / isIbanValid / bankName / ibanError analogous to the former IbanInput.
  useEffect(() => {
    const clean = stripIban(formData.iban || '')
    if (!clean) {
      setBic(null)
      setIsIbanValid(false)
      setBankName('')
      setIbanError(null)
      return
    }

    let active = true
    const timeout = setTimeout(() => {
      if (clean.length < 15) {
        setBic(null)
        setIsIbanValid(false)
        setBankName('')
        setIbanError(lang.ibanErrorMessage)
        return
      }

      BankingService.checkIban(clean)
        .then(res => {
          if (!active) return
          const { isIbanValid: ibanIsValid, bic, bankname } = res.body.data
          if (ibanIsValid) {
            setBic(bic)
            setIsIbanValid(true)
            setBankName(bankname || '')
            setIbanError(null)
          } else {
            setBic(null)
            setIsIbanValid(false)
            setBankName('')
            setIbanError(lang.ibanErrorMessage)
          }
        })
        .catch(() => {
          if (!active) return
          setBic(null)
          setIsIbanValid(false)
        })
    }, 500)

    return () => {
      active = false
      clearTimeout(timeout)
    }
  }, [formData.iban])

  useEffect(() => {
    fetchBankAccounts().then(bankAccounts => {
      const selectableAccounts = bankAccounts
        .filter(({ accountType }) => !EXCLUDED_ACCOUNT_TYPES.includes(accountType))
      if (selectableAccounts.length === 1) {
        const [bankAccount] = selectableAccounts
        const bankAccountOption = createBankAccountOption(bankAccount)
        setFormData(currentFormData => (
          currentFormData.bankAccount
            ? currentFormData
            : { ...currentFormData, bankAccount, bankAccountOption }
        ))
      }
    })
  }, [])

  useEffect(() => {
    dispatch(setBankTransferFormData({ ...formData, bic, isIbanValid }))
  }, [formData, bic, isIbanValid])

  useEffect(() => () => {
    dispatch(resetBankTransferFormData())
  }, [dispatch])

  useEffect(() => {
    const usage = formData.usage
    const usageError = checkValidSepaInput(usage) ? null : lang.expenseReceiptUsageInvalidCharacterErrorMessage
    setInputErrors(prev => (prev.usage === usageError ? prev : { ...prev, usage: usageError }))
  }, [formData.usage])

  const fetchBankAccounts = useCallback(() => dispatch(fetchBankAccountList()), [dispatch])

  const bankAccountSelectOptions = useMemo(() => ({
    defaultOptions: true,
    loadOptions: () => {
      return fetchBankAccounts()
        .then(bankAccounts => {
          return bankAccounts
            .filter(({ accountType }) => !EXCLUDED_ACCOUNT_TYPES.includes(accountType))
            .map((bankAccount) => {
              const { id, accountName, accountIban } = bankAccount
              return {
                label: `${accountName} (${accountIban ? formatIbanGroups(accountIban) : ''})`,
                value: id,
                iconUrl: prepareImageSource(bankAccount.bankLogoSmall),
                bankAccount,
              }
            })
        })
    },
  }), [fetchBankAccounts])

  const handleInputChange = useCallback((value, name) => {
    setHighlightDismissed(prev => (prev[name] ? prev : { ...prev, [name]: true }))
    if (name === 'bic') return setBic(value)

    setFormData(prev => ({ ...prev, [name]: value }))
  }, [])

  const handleUsageInput = (value, name) => {
    handleInputChange(value, name)
  }

  const handleTextAreaBlur = (target, value, name) => {
    if (name === 'usage') {
      handleUsageInput(value, name)
    }
    handleBlur()
  }

  const handleBlur = () => {
    dispatch(setBankTransferFormData({ ...formData, bic, isIbanValid }))
  }

  const onPayeeAdd = ({ type, ...addressData }) => {
    const { companyName, companyNameAffix } = addressData
    const payeeOption = { value: null, label: (`${companyName} ${companyNameAffix || ''}`).trim(), data: { addresses: [addressData] }, type }
    onPayeeChange(payeeOption)
  }

  const onPayeeChange = async(payeeOption) => {
    setSupplierCustomerSelectValue(payeeOption)

    let bankAccounts
    try {
      bankAccounts = payeeOption?.value
        ? await fetchPayeeBankAccounts(payeeOption.value, payeeOption?.type)
        : payeeOption?.data?.bankAccounts
    } catch {
      bankAccounts = null
    }

    setIbanOptions(buildIbanOptions(bankAccounts, payeeOption?.type))
    setExtraIbanOptions([])

    // Re-read the IBAN as it stands now: the user may have selected or typed a different one while
    // the payee fetch was in flight. `ibanOptions` here is the render-closure value, i.e. the
    // options that were valid when the change started, so a freshly typed IBAN is (correctly) not
    // treated as a master IBAN and is preserved below.
    const currentIban = stripIban(formDataRef.current.iban || '')
    const currentIsMasterIban = ibanOptions.some(option => option.value === currentIban)

    if (currentIsMasterIban || !currentIban) {
      if (bankAccounts?.length) {
        const [bankAccount] = bankAccounts
        const newIban = stripIban(bankAccount.iban || '')
        setFormData(prev => ({ ...prev, iban: newIban, accountHolder: bankAccount.accountHolder || payeeOption.label }))
        markAutoFilled({ iban: !!newIban, accountHolder: true })
        addExtraIbanOption(newIban)
      } else {
        setFormData(prev => ({ ...prev, iban: '', accountHolder: payeeOption.label }))
        markAutoFilled({ accountHolder: true })
      }
    } else {
      setFormData(prev => ({ ...prev, accountHolder: payeeOption.label }))
      markAutoFilled({ accountHolder: true })
      addExtraIbanOption(currentIban)
    }
  }

  const selectIbanOptions = useMemo(() => {
    const seen = new Set(ibanOptions.map(option => option.value))
    const merged = [...ibanOptions]

    extraIbanOptions.forEach(option => {
      if (!seen.has(option.value)) {
        seen.add(option.value)
        merged.push(option)
      }
    })

    const current = stripIban(formData.iban || '')
    if (current && !seen.has(current)) {
      merged.push({ value: current, label: formatIbanGroups(current) })
    }

    return merged
  }, [ibanOptions, extraIbanOptions, formData.iban])

  const doRequireContactSetup = formData.bankAccount?.accessTypeUpdateMandatory

  const showFillAnimation = (names) => {
    return names.some(name => autoFilledFields[name])
        || names.some(name => transaction?.filledData?.includes(name))
        || names.some(name => transaction?.metaData?.analysis?.indentifiedValues?.includes(name))
  }

  const ibanHighlighted = showFillAnimation(['payeeIban', 'iban']) && !highlightDismissed.iban
  const highlightStyles = useMemo(() => (
    ibanHighlighted
      ? {
        control: (base) => ({
          ...base,
          borderColor: theme.colors.yellow,
          background: theme.yellowBackground,
        }),
      }
      : null
  ), [ibanHighlighted, theme])

  return (
    <form
      className={classes.form}
      onSubmit={() => {
      }}
    >
      <BankTransferInfo transaction={transaction} />
      <div className={classes.row}>
        <SelectInput
          name='bankAccount'
          dataQsId={`${dataQsIdPrefix}-bankAccount`}
          label={lang.payments.bankTransferDrawer.bankTransferForm.fromAccount}
          containerClass={classes.bankAccount}
          customSelectOptions={bankAccountSelectOptions}
          value={formData.bankAccountOption || ''}
          handleChange={(option, name) => {
          // !Important: select input needs special change handling
            const { bankAccount } = option
            const bankAccountOption = createBankAccountOption(bankAccount)
            setFormData(prev => ({ ...prev, [name]: bankAccount, bankAccountOption }))
          }}
          required
        />
      </div>
      <div className={classes.defaultContainer}>
        {doRequireContactSetup && <ContactSetupRequiredEmbeddedDialog />}
        <div className={classes.row}>
          <SupplierCustomerSelect
            name='payee'
            dataQsId={`${dataQsIdPrefix}-payee`}
            label={lang.payments.bankTransferDrawer.bankTransferForm.supplierCustomer}
            placeholder={lang.receipt.form.payee.placeholder}
            loadingMessage={() => lang.receipt.form.payee.loadingMessage}
            autoFocus={false}
            value={supplierCustomerSelectValue}
            inputValue={supplierInputValue}
            onInputChange={setSupplierInputValue}
            handleAdd={onPayeeAdd}
            handleChange={onPayeeChange}
          />
        </div>
        <div className={classes.row}>
          <TextInput
            name='accountHolder'
            useFormik={false}
            dataQsId={`${dataQsIdPrefix}-accountHolder`}
            customClass={showFillAnimation(['payeeName', 'accountHolder']) && !highlightDismissed.accountHolder && classes.highlight}
            fillAnimationOptions={{ show: showFillAnimation(['payeeName', 'accountHolder']) && !highlightDismissed.accountHolder, color: theme.colors.yellow }}
            label={lang.payments.bankTransferDrawer.bankTransferForm.recipient}
            value={formData.accountHolder || transaction?.payeeName || ''}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
          />
        </div>
        <div className={clsx(classes.row, classes.ibanRow)}>
          <SelectInput
            name='iban'
            notAsync
            allowCreate
            createOnBlur
            useFormik={false}
            dataQsId={`${dataQsIdPrefix}-iban`}
            label={lang.payments.bankTransferDrawer.bankTransferForm.iban.label}
            options={selectIbanOptions}
            value={formData.iban || ''}
            errorMessage={ibanError}
            styles={highlightStyles}
            fillAnimationOptions={{ show: showFillAnimation(['payeeIban', 'iban']) && !highlightDismissed.iban, color: theme.colors.yellow }}
            customSelectOptions={customSelectOptions}
            handleChange={(option) => {
              setHighlightDismissed(prev => ({ ...prev, iban: true }))
              setFormData(prev => ({ ...prev, iban: option ? stripIban(option.value) : '' }))
            }}
            onOptionCreated={(inputValue) => {
              const iban = stripIban(inputValue)
              if (!iban) return
              setHighlightDismissed(prev => ({ ...prev, iban: true }))
              setFormData(prev => ({ ...prev, iban }))
            }}
            onBlur={handleBlur}
            required
          />
          <div className={classes.creditInstitutionWrapper}>
            <div className={classes.creditInstitutionLabel}>{`${lang.billingDataForm.creditInstitution}: `}</div>
            <div className={classes.bankName}>{bankName || lang.billingDataForm.creditInstitutionUnknown}</div>
          </div>
        </div>
        <div className={classes.row}>
          <CurrencyInput
            customClass={classes.currencyInput}
            name='amount'
            readOnly
            dataQsId={`${dataQsIdPrefix}-amount`}
            label={lang.payments.bankTransferDrawer.bankTransferForm.amount}
            value={formData.amount}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
          />
        </div>
        <div className={classes.row}>
          <TextareaInput
            name='usage'
            useFormik={false}
            dataQsId={`${dataQsIdPrefix}-usage`}
            label={lang.payments.bankTransferDrawer.bankTransferForm.usage}
            placeholder={lang.payments.bankTransferDrawer.bankTransferForm.usage}
            maxLength={140}
            rows={1}
            value={formData.usage}
            errorMessage={inputErrors.usage}
            onChange={handleUsageInput}
            onBlur={handleTextAreaBlur}
            className={classes.usage}
            required
          />
        </div>
        <div className={classes.row}>
          <TextInput
            name='executionDate'
            useFormik={false}
            dataQsId={`${dataQsIdPrefix}-executionDate`}
            label={lang.payments.bankTransferDrawer.bankTransferForm.execution}
            value={lang.payments.bankTransferDrawer.bankTransferForm.executionDirect}
            onChange={handleInputChange}
            onBlur={handleBlur}
            readOnly={true}
            readOnlyLabelEnabled={true}
            required
            disabled
          />
          {/* for later purpose, when executionDate can be setted
          <DateInput
            name='executionDate'
            label={lang.payments.bankTransferDrawer.bankTransferForm.execution}
            value={formData.executionDate}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
          /> */}
          <Checkbox
            dataQsId={`${dataQsIdPrefix}-realTimeTransfer`}
            customClass={classes.checkBox}
            checked={formData.realTimeTransfer}
            name='realTimeTransfer'
            onChange={handleInputChange}>
            <span className={classes.checkboxLabel}>Echtzeit Überweisung</span>
          </Checkbox>
        </div>
      </div>
    </form>
  )
}

BankTransferForm.propTypes = {
  transaction: PropTypes.object.isRequired,
}

// This is a snapshot form: it reads `transaction` once on mount (prefill) and otherwise only for
// display. Callers (e.g. ExpenseEditPage) may pass a new transaction object on every render while
// their own state churns. Comparing by transaction identity keeps the form from re-rendering for
// an unchanged transaction; a different transaction (new open) still mounts fresh / re-renders.
const arePropsEqual = (prevProps, nextProps) => prevProps.transaction?.id === nextProps.transaction?.id

export default React.memo(BankTransferForm, arePropsEqual)
